<template>
  <div class="calendar-container">
    <div class="flex flex-col md:flex-row h-full">
      <!-- Sidebar with mini calendar -->
      <div
        v-if="showSidebar"
        class="sidebar w-full md:w-64 border-r border-gray-200 p-4 bg-gray-50"
      >
        <div v-if="showMiniCalendar" class="mb-4">
          <div class="flex justify-between items-center mb-2">
            <h2 class="text-lg font-semibold">
              {{ "helllo" }}
              {{ formatMonth(currentDate) }}
            </h2>
            <div class="flex space-x-1">
              <button @click="prevMonth" class="p-1 rounded hover:bg-gray-200">
                <ChevronLeftIcon class="h-5 w-5 text-gray-600" />
              </button>
              <button @click="nextMonth" class="p-1 rounded hover:bg-gray-200">
                <ChevronRightIcon class="h-5 w-5 text-gray-600" />
              </button>
            </div>
          </div>
          <div class="mini-calendar">
            <div
              class="grid grid-cols-7 text-center text-xs text-gray-500 mb-1"
            >
              <div v-for="day in weekDays" :key="day">{{ day }}</div>
            </div>
            <div class="grid grid-cols-7 gap-1">
              <div
                v-for="(day, index) in miniCalendarDays"
                :key="index"
                class="mini-day text-center py-1 text-xs rounded-full cursor-pointer"
                :class="{
                  'text-gray-400': day.month !== 'current',
                  'bg-primary text-white': isSameDay(day.date, selectedDate),
                  'hover:bg-gray-200': !isSameDay(day.date, selectedDate),
                  'bg-red-100': isHoliday(day.date),
                }"
                @click="selectDate(day.date)"
                @dblclick="openEventForm(null, day.date)"
              >
                {{ day.dayNumber }}
              </div>
            </div>
          </div>
        </div>
        <div v-if="showUpcomingEvents" class="upcoming-events">
          <h3 class="text-md font-medium mb-2">Upcoming Events</h3>
          <div v-if="upcomingEvents.length === 0" class="text-sm text-gray-500">
            No upcoming events
          </div>
          <div
            v-for="event in upcomingEvents"
            :key="event.id"
            class="event-item p-2 mb-2 rounded text-sm cursor-pointer"
            :class="dashboardStore.getEventColorClass(event)"
            @click="selectEvent(event)"
          >
            <div v-if="event.title">
              <strong>Title:</strong> {{ event.title }}
            </div>
            <div v-if="event.customer_name">
              <strong>Customer:</strong> {{ event.customer_name }}
            </div>
            <div v-if="event.order_no">
              <strong>Order No:</strong> {{ event.order_no }}
            </div>
            <div class="text-xs">{{ formatDate(event.start) }}</div>
          </div>
        </div>
      </div>

      <!-- Main calendar area -->
      <div class="calendar-main flex-1 flex flex-col">
        <!-- Calendar header with controls -->
        <div class="calendar-header border-b border-gray-200 p-4">
          <div class="flex flex-wrap justify-between items-center">
            <div class="flex items-center space-x-4 mb-2 md:mb-0">
              <button
                v-if="allowAddEvents"
                class="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
                @click="openEventForm(null)"
              >
                <PlusIcon class="h-5 w-5 inline-block mr-1" />
                Add Event
              </button>
              <button
                class="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-100"
                @click="goToToday"
              >
                Today
              </button>
              <button
                class="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-100"
                @click="refreshCalendar"
              >
                <ArrowPathIcon class="h-5 w-5" />
              </button>
              <div class="flex space-x-1">
                <button
                  @click="prevPeriod"
                  class="p-1 rounded hover:bg-gray-200"
                >
                  <ChevronLeftIcon class="h-5 w-5 text-gray-600" />
                </button>
                <button
                  @click="nextPeriod"
                  class="p-1 rounded hover:bg-gray-200"
                >
                  <ChevronRightIcon class="h-5 w-5 text-gray-600" />
                </button>
              </div>

              <h2 class="text-xl font-semibold">
                {{ calendarTitle }}
              </h2>

              <!-- Year selector moved here -->
              <div v-if="currentView === 'year'" class="flex items-center ml-2">
                <select
                  v-model="selectedYear"
                  class="form-select rounded border border-gray-300 py-1 px-2"
                  data-year-selector
                >
                  <option v-for="year in yearOptions" :key="year" :value="year">
                    {{ year }}
                  </option>
                </select>
              </div>
            </div>

            <div
              class="view-controls flex space-x-1 border rounded-md overflow-hidden"
            >
              <button
                v-for="view in viewOptions"
                :key="view.value"
                @click="changeView(view.value)"
                class="px-3 py-1.5 text-sm"
                :class="
                  currentView === view.value
                    ? 'bg-gray-200'
                    : 'hover:bg-gray-100'
                "
              >
                {{ view.label }}
              </button>
            </div>

            <div class="flex items-center space-x-2 ml-2">
              <label class="text-sm text-gray-600">Holidays:</label>
              <select
                v-model="dashboardStore.selectedHolidayCountryCode"
                class="form-select rounded border border-gray-300 py-1 px-2 text-sm"
                @change="selectHolidayCountry"
              >
                <option value="">None</option>
                <option value="dk">Danish</option>
                <option value="pk">Pakistani</option>
                <option value="us">US</option>
                <option value="gb">UK</option>
                <option value="de">German</option>
              </select>
            </div>
          </div>
        </div>

        <!-- Calendar content -->
        <div class="calendar-content flex-1 overflow-auto">
          <!-- Day view -->
          <DayView
            v-if="currentView === 'day'"
            :date="selectedDate"
            :events="filteredEvents"
            :editable="allowEditEvents"
            :deletable="allowDeleteEvents"
            :holidays="holidays"
            :eventTemplate="eventTemplate"
            @event-click="selectEvent"
            @time-slot-click="createEventAtTime"
            @event-drop="updateEventTime"
            @event-delete="confirmDeleteEvent"
            @dblclick-date="openEventForm(null, $event)"
          >
            <template #event-tile="eventProps">
              <slot name="event-tile" v-bind="eventProps"></slot>
            </template>
          </DayView>

          <!-- Week view -->
          <WeekView
            v-if="currentView === 'week'"
            :date="selectedDate"
            :selectedDate="selectedDate"
            :events="filteredEvents"
            :editable="allowEditEvents"
            :deletable="allowDeleteEvents"
            :holidays="holidays"
            :eventTemplate="eventTemplate"
            @date-click="selectDate"
            @event-click="selectEvent"
            @event-drop="updateEventDate"
            @event-delete="confirmDeleteEvent"
            @dblclick-date="openEventForm(null, $event)"
          >
            <template #event-tile="eventProps">
              <slot name="event-tile" v-bind="eventProps"></slot>
            </template>
          </WeekView>

          <!-- Month view -->
          <MonthView
            v-if="currentView === 'month'"
            :date="selectedDate"
            :events="filteredEvents"
            :editable="allowEditEvents"
            :deletable="allowDeleteEvents"
            :holidays="holidays"
            :maxEventsPerDay="maxEventsPerDay"
            :selectedDate="selectedDate"
            :eventTemplate="eventTemplate"
            @date-click="selectDate"
            @event-click="selectEvent"
            @event-drop="updateEventDate"
            @event-delete="confirmDeleteEvent"
            @mark-holiday="toggleHoliday"
            @switch-to-day-view="switchToDayView"
            @dblclick-date="openEventForm(null, $event)"
          >
            <template #event-tile="eventProps">
              <slot name="event-tile" v-bind="eventProps"></slot>
            </template>
          </MonthView>

          <!-- Year view -->
          <YearView
            v-if="currentView === 'year'"
            :date="selectedDate"
            :events="filteredEvents"
            :holidays="holidays"
            @month-click="selectMonth"
            @dblclick-date="openEventForm(null, $event)"
          />
        </div>
      </div>
    </div>

    <!-- Event form modal using Dialog component -->
    <Teleport to="body">
      <Dialog
        v-if="showEventForm"
        :title="eventDialogTitle"
        :showCloseButton="true"
        :showFooter="!editingEvent?.type || editingEvent.type !== CalendarEventType.Order"
        :customFooter="false"
        :showSaveButton="!editingEvent?.type || editingEvent.type !== CalendarEventType.Order"
        :showCancelButton="!editingEvent?.type || editingEvent.type !== CalendarEventType.Order"
        saveButtonText="Save"
        cancelButtonText="Cancel"
        :md="true"
        @closeEvent="closeEventForm"
        @saveEvent="saveEvent(eventForm)"
        @cancelEvent="closeEventForm"
        :saveButtonDisabled="isEventSaveDisabled"
      >
        <template #modal-content>
          <!-- Use slot if provided, otherwise use default form -->
          <slot
            name="event-form"
            :event="editingEvent"
            :close="closeEventForm"
            :save="saveEvent"
            :delete="confirmDeleteEvent"
          >
            <form @submit.prevent="saveEvent(eventForm)">
              <!-- Order ID field -->
              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-1"
                  >Order No</label
                >
                <Select
                  :options="orderOptions"
                  track-by="id"
                  label="order_no"
                  v-model="selectedOrder"
                  class="w-full"
                  :filterable="true"
                  :clearable="true"
                  @search="onOrderSearch"
                  :multiple="false"
                  :disabled="!!editingEvent"
                />
              </div>

              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-1"
                  >Order Title</label
                >
                <input
                  :value="orderInfo.order_title"
                  type="text"
                  class="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-100"
                  readonly
                />
              </div>

              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-1"
                  >Customer</label
                >
                <input
                  :value="orderInfo.customer"
                  type="text"
                  class="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-100"
                  readonly
                />
              </div>

              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-1"
                  >Shipping Address</label
                >
                <input
                  :value="orderInfo.shipping_address"
                  type="text"
                  class="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-100"
                  readonly
                />
              </div>

              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <template v-if="editingEvent?.type === CalendarEventType.Order">
                  <input
                    type="text"
                    v-model="eventForm.status"
                    class="w-full border border-gray-300 rounded-md px-3 py-2"
                    readonly
                  />
                </template>
                <template v-else>
                  <select
                    v-model="eventForm.status"
                    class="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="pending">Pending</option>
                    <option value="shipped">Shipped</option>
                    <option value="delayed">Delayed</option>
                    <option value="completed">Completed</option>
                  </select>
                </template>
              </div>

              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-1"
                  >Notes</label
                >
                <textarea
                  v-model="eventForm.notes"
                  class="w-full border border-gray-300 rounded-md px-3 py-2 h-20"
                  :readonly="editingEvent?.type === CalendarEventType.Order"
                ></textarea>
              </div>

              <div
                v-if="editingEvent && allowDeleteEvents"
                class="flex justify-end"
              >
                <button
                  type="button"
                  @click="confirmDeleteEvent(editingEvent.id)"
                  class="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                  v-if="!editingEvent?.type || editingEvent.type !== CalendarEventType.Order"
                >
                  Delete
                </button>
              </div>
            </form>
          </slot>
        </template>
      </Dialog>
    </Teleport>

    <!-- Holiday marking modal using Dialog component -->
    <Teleport to="body">
      <Dialog
        v-if="showHolidayModal"
        title="Mark as Holiday"
        :showCloseButton="true"
        :showFooter="true"
        :customFooter="false"
        :showSaveButton="true"
        :showCancelButton="true"
        saveButtonText="Save"
        cancelButtonText="Cancel"
        :saveButtonDisabled="isHolidaySaveDisabled"
        :md="true"
        @closeEvent="closeHolidayModal"
        @saveEvent="saveHoliday"
        @cancelEvent="closeHolidayModal"
      >
        <template #modal-content>
          <div
            v-if="!dashboardStore.selectedHolidayCountryCode"
            class="mb-4 text-red-500 text-sm font-medium flex justify-center"
          >
            Select any holiday country type
          </div>
          <form @submit.prevent="saveHoliday">
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Holiday Name</label
              >
              <input
                v-model="holidayForm.name"
                type="text"
                class="w-full border border-gray-300 rounded-md px-3 py-2"
                required
              />
            </div>

            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Start Date</label
              >
              <input
                v-model="holidayForm.date"
                type="date"
                class="w-full border border-gray-300 rounded-md px-3 py-2"
                required
              />
            </div>

            <div class="mb-4">
              <div class="flex items-center mb-2">
                <input
                  type="checkbox"
                  id="dateRange"
                  v-model="holidayForm.isDateRange"
                  class="mr-2"
                />
                <label for="dateRange" class="text-sm font-medium text-gray-700"
                  >Date Range</label
                >
              </div>

              <div v-if="holidayForm.isDateRange" class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-1"
                  >End Date</label
                >
                <input
                  v-model="holidayForm.endDate"
                  type="date"
                  class="w-full border border-gray-300 rounded-md px-3 py-2"
                  required
                  :min="holidayForm.date"
                />
              </div>
            </div>

            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Type</label
              >
              <select
                v-model="holidayForm.type"
                class="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="public">Public Holiday</option>
                <option value="company">Company Holiday</option>
                <option value="personal">Personal Holiday</option>
              </select>
            </div>

            <div v-if="isExistingHoliday" class="flex justify-end">
              <button
                type="button"
                @click="removeHoliday"
                class="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
              >
                Remove Holiday
              </button>
            </div>
          </form>
        </template>
      </Dialog>
    </Teleport>

    <!-- Too many events modal using Dialog component -->
    <Teleport to="body">
      <Dialog
        v-if="showTooManyEventsModal"
        :title="`Events for ${tooManyEventsDate ? format(tooManyEventsDate, 'MMMM d, yyyy') : ''}`"
        :showCloseButton="true"
        :showFooter="true"
        :customFooter="true"
        :lg="true"
        @closeEvent="closeTooManyEventsModal"
      >
        <template #modal-content>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div
              v-for="event in tooManyEvents"
              :key="event.id"
              class="event-item p-2 rounded text-sm cursor-pointer group relative"
              :class="dashboardStore.getEventColorClass(event)"
              @click="selectEvent(event)"
            >
              <!-- Delete button -->
              <button
                v-if="allowDeleteEvents"
                class="absolute top-1 right-1 bg-white rounded-full p-0.5 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                @click.stop="confirmDeleteEvent(event.id)"
              >
                <XMarkIcon class="h-3 w-3 text-red-500" />
              </button>

              <div class="font-medium">{{ event.title }}</div>
              <div v-if="event.time" class="text-xs">{{ event.time }}</div>
              <div v-if="event.type" class="text-xs">
                Type: {{ event.type }}
              </div>
              <div v-if="event.status" class="text-xs">
                Status: {{ event.status }}
              </div>
              <div v-if="event.notes" class="text-xs mt-1 text-gray-600">
                {{ event.notes }}
              </div>
            </div>
          </div>
        </template>
        <template #footer>
          <button
            class="px-3 py-1 text-sm bg-primary text-white rounded-md hover:bg-primary-dark"
            @click="switchToDayView(tooManyEventsDate)"
          >
            Switch to Day View
          </button>
          <button
            class="ml-2 px-3 py-1 text-sm bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
            @click="closeTooManyEventsModal"
          >
            Close
          </button>
        </template>
      </Dialog>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import {
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  XMarkIcon,
} from "@heroicons/vue/24/outline";
import {
  addDays,
  addMonths,
  addYears,
  differenceInDays,
  eachDayOfInterval,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  getMonth,
  getWeek,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  parseISO,
  setHours,
  setMinutes,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subMonths,
  subYears,
} from "date-fns";
import Swal from "sweetalert2";
import { computed, nextTick, onMounted, ref, watch } from "vue";

// Import sub-components
import Dialog from "@/components/Shared/Dialog.vue";
import {
  CalendarEventType,
  HolidayType,
  OrderStatus,
  useDebouncedSearch,
} from "@/composables";
import { http } from "@/interceptors/axiosRequestInterceptor";
import { Event, useDashboardStore } from "@/store/dashboard";
import { Console } from "console";
import DayView from "./components/DayView.vue";
import MonthView from "./components/MonthView.vue";
import WeekView from "./components/WeekView.vue";
import YearView from "./components/YearView.vue";
import Select from "./Select.vue";
// Define types

type EventType =
  | CalendarEventType
  | "reminder"
  | "meeting"
  | "appointment"
  | "other";
interface EventFormData {
  id: string;
  title: string;
  start: string;
  end?: string;
  time?: string;
  type?: EventType;
  status: OrderStatus;
  notes?: string;
  allDay?: boolean;
  [key: string]: any;
}

interface CalendarDay {
  date: Date;
  dayNumber: number;
  month: "prev" | "current" | "next";
}

interface Holiday {
  id: string;
  name: string;
  date: string;
  type: HolidayType;
  country_code?: string;
}

interface HolidayFormData {
  id?: string;
  name: string;
  date: string;
  endDate?: string;
  isDateRange: boolean;
  type: HolidayType;
}

// Add a local Event interface for calendar views
interface LocalEvent {
  id: string;
  title: string;
  start: string;
  end?: string;
  time?: string;
  type?: EventType;
  status: OrderStatus;
  notes?: string;
  allDay?: boolean;
  [key: string]: any;
}

// Define props
const props = defineProps({
  initialEvents: {
    type: Array as () => Event[],
    default: () => [],
  },
  initialHolidays: {
    type: Array as () => Holiday[],
    default: () => [],
  },
  allowAddEvents: {
    type: Boolean,
    default: true,
  },
  allowEditEvents: {
    type: Boolean,
    default: true,
  },
  allowDeleteEvents: {
    type: Boolean,
    default: true,
  },
  showSidebar: {
    type: Boolean,
    default: true,
  },
  showMiniCalendar: {
    type: Boolean,
    default: true,
  },
  showUpcomingEvents: {
    type: Boolean,
    default: true,
  },
  initialView: {
    type: String as () => "day" | "week" | "month" | "year",
    default: "month",
  },
  maxEventsPerDay: {
    type: Number,
    default: 5,
  },
  eventTemplate: {
    type: Object,
    default: () => ({
      showTitle: true,
      showTime: true,
      showType: false,
      showStatus: false,
      showNotes: false,
      titleLines: 1,
      notesLines: 0,
      showOrderNo: true,
    }),
  },
  orderNoSearch: {
    type: String,
    default: "",
  },
  eventTypeFilter: {
    type: String,
    default: "",
  },
  eventStatusFilter: {
    type: String,
    default: "",
  },
  selectedDate: {
    type: Date,
    default: null,
  },
});

// Define emits
const emit = defineEmits([
  "event-updated",
  "event-created",
  "event-deleted",
  "holiday-added",
  "holiday-updated",
  "holiday-removed",
  "refresh-requested",
  "view-changed",
  "date-selected",
]);

// State
const events = ref<Event[]>(
  props.initialEvents
    ? props.initialEvents.map((e) => ({
        ...e,
        status: e.status as Event["status"],
        type:
          typeof e.type === "string"
            ? CalendarEventType[
                (e.type.charAt(0).toUpperCase() +
                  e.type.slice(1)) as keyof typeof CalendarEventType
              ] || CalendarEventType.Event
            : e.type,
      }))
    : [],
);
const holidays = ref<Holiday[]>(
  props.initialHolidays
    ? props.initialHolidays.map((h) => ({
        ...h,
        type:
          typeof h.type === "string"
            ? HolidayType[
                (h.type.charAt(0).toUpperCase() +
                  h.type.slice(1)) as keyof typeof HolidayType
              ] || HolidayType.Public
            : h.type,
      }))
    : [],
);
const currentDate = ref(new Date());
const selectedDate = ref(
  props.selectedDate ? new Date(props.selectedDate) : new Date(),
);
const currentView = ref<"day" | "week" | "month" | "year">(props.initialView);
const showEventForm = ref(false);
const editingEvent = ref<Event | null>(null);
const selectedYear = computed({
  get: () => selectedDate.value.getFullYear(),
  set: (year: number) => {
    const newDate = new Date(selectedDate.value);
    newDate.setFullYear(year);
    selectedDate.value = newDate;
    currentDate.value = newDate;

    nextTick(() => {
      emit("view-changed", {
        view: currentView.value,
        selected_date: selectedDate.value,
        selected_holiday_country_code:
          dashboardStore.selectedHolidayCountryCode,
      });
    });
  },
});
const showHolidayModal = ref(false);
const holidayDate = ref<Date | null>(null);
const isExistingHoliday = ref(false);

const isLoadingHolidays = ref(false);
const showTooManyEventsModal = ref(false);
const tooManyEventsDate = ref<Date | null>(null);
const tooManyEvents = ref<Event[]>([]);
const dashboardStore = useDashboardStore();
const orderInfo = computed(() => dashboardStore.orderInfo);

// Order search and info
const orderList = computed(() => dashboardStore.orderList || []);
const selectedOrder = computed({
  get: () => dashboardStore.selectedOrder,
  set: (value) => {
    if (dashboardStore.setSelectedOrder) {
      dashboardStore.setSelectedOrder(value);
    }
  },
});
const isOrderLoading = computed(() => dashboardStore.isOrderLoading);

const orderOptions = computed(() =>
  orderList.value.map((order) => ({
    ...order,
    label: order.customer_reference_no
      ? `${order.order_no} (${order.customer_reference_no})`
      : order.order_no,
  })),
);

// Event form
const eventForm = ref<EventFormData>({
  id: "",
  title: "",
  start: format(new Date(), "yyyy-MM-dd"),
  time: format(new Date(), "HH:mm"),
  type: CalendarEventType.Event as EventType,
  status: OrderStatus.Pending,
  notes: "",
  allDay: false,
});

// Holiday form
const holidayForm = ref<HolidayFormData>({
  name: "",
  date: format(new Date(), "yyyy-MM-dd"),
  endDate: format(addDays(new Date(), 1), "yyyy-MM-dd"),
  isDateRange: false,
  type: HolidayType.Public,
});
const isHolidaySaveDisabled = computed(() => {
  return !holidayForm.value.name || !dashboardStore.selectedHolidayCountryCode;
});
const isEventSaveDisabled = computed(() => {
  return !orderInfo.value.order_title || !orderInfo.value.id;
});
// Watch for prop changes
watch(
  () => props.initialEvents,
  (newEvents) => {
    if (newEvents && Array.isArray(newEvents)) {
      events.value = newEvents.map((e) => ({
        ...e,
        status: e.status as Event["status"],
        type:
          typeof e.type === "string"
            ? CalendarEventType[
                (e.type.charAt(0).toUpperCase() +
                  e.type.slice(1)) as keyof typeof CalendarEventType
              ] || CalendarEventType.Event
            : e.type,
      }));
    } else {
      events.value = [];
    }
  },
  { deep: true, immediate: true },
);

watch(
  () => props.initialHolidays,
  (newHolidays) => {
    if (newHolidays && Array.isArray(newHolidays)) {
      holidays.value = newHolidays.map((h) => ({
        ...h,
        type:
          typeof h.type === "string"
            ? HolidayType[
                (h.type.charAt(0).toUpperCase() +
                  h.type.slice(1)) as keyof typeof HolidayType
              ] || HolidayType.Public
            : h.type,
      }));
    } else {
      holidays.value = [];
    }
  },
  { deep: true },
);

// Watch for store events changes to sync with local events
watch(
  () => dashboardStore.events,
  (newEvents) => {
    if (newEvents && Array.isArray(newEvents) && newEvents.length > 0) {
      events.value = newEvents.map((e) => ({
        ...e,
        status: e.status as Event["status"],
        type:
          typeof e.type === "string"
            ? CalendarEventType[
                (e.type.charAt(0).toUpperCase() +
                  e.type.slice(1)) as keyof typeof CalendarEventType
              ] || CalendarEventType.Event
            : e.type,
      }));
    }
  },
  { deep: true },
);

// Watch for store holidays changes to sync with local holidays
watch(
  () => dashboardStore.holidays,
  (newHolidays) => {
    if (newHolidays && Array.isArray(newHolidays) && newHolidays.length > 0) {
      holidays.value = newHolidays.map((h) => ({
        ...h,
        type:
          typeof h.type === "string"
            ? HolidayType[
                (h.type.charAt(0).toUpperCase() +
                  h.type.slice(1)) as keyof typeof HolidayType
              ] || HolidayType.Public
            : h.type,
      }));
    }
  },
  { deep: true },
);

watch(
  () => props.initialView,
  (newView) => {
    currentView.value = newView;
  },
);
watch(
  () => props.selectedDate,
  (newDate, oldDate) => {
    if (newDate) {
      selectedDate.value = new Date(newDate);
      currentDate.value = new Date(newDate);
    }
  },
);

const refreshCalendar = () => {
  if (dashboardStore.fetchCalendarData) {
    dashboardStore.fetchCalendarData();
  } else {
    console.error("dashboardStore.fetchCalendarData is not available");
  }
  emit("refresh-requested");
};

const openEventForm = (event: Event | null, date?: Date) => {
  editingEvent.value = event;
  if (event && event.order_id) {
    let foundOrder = orderList.value.find((o) => o.order_id === event.order_id);
    if (!foundOrder) {
      selectedOrder.value = event;
    } else {
      selectedOrder.value = foundOrder;
      dashboardStore.setSelectedOrder(foundOrder);
    }

    // Set orderInfo from event fields if present
    dashboardStore.orderInfo = {
      order_id: event.order_id ?? "",
      order_no: event.order_no ?? "",
      order_title: event.order_title ?? "",
      customer: event.customer ?? event.customer_name ?? "",
      shipping_address: event.shipping_address ?? "",
      status: event.status ?? "pending",
      notes: event.notes ?? "",
      start: event.start ?? "",
      id: event.id ?? "",
    };

    // For editing existing events, preserve the original event data
    eventForm.value = {
      id: event.id,
      title: event.title || "",
      start: event.start, // Preserve original start date
      time: event.time || format(new Date(), "HH:mm"),
      type: (event.type as EventType) || CalendarEventType.Event,
      status: (event.status as OrderStatus) || OrderStatus.Pending, // Preserve original status
      notes: event.notes || "",
      allDay: event.allDay || false,
      order_id: event.order_id,
      order_no: event.order_no,
      order_title: event.order_title,
      customer: event.customer,
      shipping_address: event.shipping_address,
    };
  } else {
    selectedOrder.value = null;
    resetEventForm();
    if (date) {
      eventForm.value.start = format(date, "yyyy-MM-dd");
    } else {
      eventForm.value.start = format(new Date(), "yyyy-MM-dd");
    }
  }

  if (
    selectedOrder.value &&
    !orderList.value.some((o) => o.id === selectedOrder.value.id)
  ) {
    // Use the store's setOrderList method instead of directly pushing
    const updatedOrderList = [...orderList.value, selectedOrder.value];
    if (dashboardStore.setOrderList) {
      dashboardStore.setOrderList(updatedOrderList);
    }
  }

  showEventForm.value = true;
};

const closeEventForm = () => {
  showEventForm.value = false;
  editingEvent.value = null;
  selectedOrder.value = null;
  resetEventForm();
};

const saveEvent = (formData: EventFormData = eventForm.value) => {
  eventForm.value.order_id = orderInfo.value.order_id;
  eventForm.value.order_no = orderInfo.value.order_no;
  eventForm.value.order_title = orderInfo.value.order_title;
  eventForm.value.customer = orderInfo.value.customer;
  eventForm.value.shipping_address = orderInfo.value.shipping_address;
  eventForm.value.type = (eventForm.value.type ||
    CalendarEventType.Event) as EventType;
  eventForm.value.status = (eventForm.value.status ||
    OrderStatus.Pending) as EventFormData["status"];
  if (editingEvent.value) {
    eventForm.value.id = editingEvent.value.id;
  } else {
    if (!eventForm.value.status) {
      eventForm.value.status = (orderInfo.value.status ||
        OrderStatus.Pending) as EventFormData["status"];
    }
    if (!eventForm.value.notes) {
      eventForm.value.notes = orderInfo.value.notes || "";
    }
  }

  const eventData = { ...eventForm.value };

  if (editingEvent.value) {
    const index = events.value.findIndex((e) => e.id === eventData.id);
    if (index !== -1) {
      events.value[index] = eventData;
      emit("event-updated", eventData);
    }
  } else {
    events.value.push(eventData);
    emit("event-created", eventData);
  }
  closeEventForm();
};

const deleteEvent = (eventId: string) => {
  const index = events.value.findIndex((e) => e.id === eventId);
  if (index !== -1) {
    const deletedEvent = events.value[index];
    events.value.splice(index, 1);
    emit("event-deleted", eventId);
  }

  closeEventForm();
};

const selectEvent = (event: Event) => {
  // if (props.allowEditEvents && event.type !== CalendarEventType.Order) {
    openEventForm(event);
  // }
};

const createEventAtTime = (date: Date) => {
  if (props.allowAddEvents) {
    openEventForm(null, date);
  }
};

const updateEventTime = (eventId: string, newStart: Date) => {
  if (!props.allowEditEvents) return;

  const event = events.value.find((e) => e.id === eventId);
  if (event) {
    event.start = format(newStart, "yyyy-MM-dd");
    event.time = format(newStart, "HH:mm");

    // Create payload with additional fields for drag and drop
    const updatedEvent = {
      ...event,
      new_date: format(newStart, "yyyy-MM-dd"),
      event_id: event.id || null,
    };

    emit("event-updated", updatedEvent);
  } else {
    console.error("Event not found for ID:", eventId);
  }
};

const updateEventDate = (eventId: string, newDate: Date) => {
  if (!props.allowEditEvents) return;

  const event = events.value.find((e) => e.id === eventId);
  if (event) {
    event.start = format(newDate, "yyyy-MM-dd");

    // Create payload with additional fields for drag and drop
    const updatedEvent = {
      ...event,
      new_date: format(newDate, "yyyy-MM-dd"),
      event_id: event.id || null,
    };

    emit("event-updated", updatedEvent);
  } else {
    console.error("Event not found for ID:", eventId);
  }
};

const generateId = () => {
  return "event_" + Math.random().toString(36).substr(2, 9);
};

// Holiday methods
const isHoliday = (date: Date) => {
  return holidays.value.some((holiday) => {
    const holidayDate = parseISO(holiday.date);
    return isSameDay(holidayDate, date);
  });
};

const toggleHoliday = (date: Date) => {
  holidayDate.value = date;
  const existingHoliday = holidays.value.find((holiday) => {
    const holidayDate = parseISO(holiday.date);
    return isSameDay(holidayDate, date);
  });
  if (existingHoliday) {
    isExistingHoliday.value = true;
    holidayForm.value = {
      id: existingHoliday.id,
      name: existingHoliday.name,
      date: format(date, "yyyy-MM-dd"),
      endDate: format(addDays(date, 1), "yyyy-MM-dd"),
      isDateRange: false,
      type: existingHoliday.type,
    };
  } else {
    isExistingHoliday.value = false;
    holidayForm.value = {
      name: "",
      date: format(date, "yyyy-MM-dd"),
      endDate: format(addDays(date, 1), "yyyy-MM-dd"),
      isDateRange: false,
      type: HolidayType.Public,
    };
  }

  showHolidayModal.value = true;
};

const closeHolidayModal = () => {
  showHolidayModal.value = false;
  holidayDate.value = null;
};

const saveHoliday = () => {
  if (isExistingHoliday.value && holidayForm.value.id) {
    // Update existing holiday
    const index = holidays.value.findIndex(
      (h) => h.id === holidayForm.value.id,
    );
    if (index !== -1) {
      const updatedHoliday = {
        id: holidayForm.value.id,
        name: holidayForm.value.name,
        date: holidayForm.value.date,
        type: holidayForm.value.type,
        country_code: dashboardStore.selectedHolidayCountryCode,
      };
      holidays.value[index] = updatedHoliday;
      emit("holiday-updated", updatedHoliday);
    }
  } else {
    // Create new holiday(s)
    if (holidayForm.value.isDateRange && holidayForm.value.endDate) {
      // Create a holiday for each day in the range
      const startDate = parseISO(holidayForm.value.date);
      const endDate = parseISO(holidayForm.value.endDate);

      const daysInRange = eachDayOfInterval({ start: startDate, end: endDate });

      daysInRange.forEach((day, index) => {
        const newHoliday: Holiday = {
          id: generateId(),
          name: `${holidayForm.value.name}${daysInRange.length > 1 ? ` (Day ${index + 1})` : ""}`,
          date: format(day, "yyyy-MM-dd"),
          type: holidayForm.value.type,
          country_code: dashboardStore.selectedHolidayCountryCode,
        };
        holidays.value.push(newHoliday);
        emit("holiday-added", newHoliday);
      });
    } else {
      // Create a single holiday
      const newHoliday: Holiday = {
        id: generateId(),
        name: holidayForm.value.name,
        date: holidayForm.value.date,
        type: holidayForm.value.type,
        country_code: dashboardStore.selectedHolidayCountryCode,
      };
      holidays.value.push(newHoliday);
      emit("holiday-added", newHoliday);
    }
  }

  closeHolidayModal();
};

const removeHoliday = () => {
  if (holidayForm.value.id) {
    const index = holidays.value.findIndex(
      (h) => h.id === holidayForm.value.id,
    );
    if (index !== -1) {
      const removedHoliday = holidays.value[index];
      holidays.value.splice(index, 1);
      emit("holiday-removed", removedHoliday);
    }
  }

  closeHolidayModal();
};

const switchToDayView = (date: Date) => {
  selectedDate.value = date;
  currentDate.value = date;
  currentView.value = "day";
  emit("view-changed", {
    view: "day",
    selected_date: selectedDate.value,
    selected_holiday_country_code: dashboardStore.selectedHolidayCountryCode,
  });
};

const confirmDeleteEvent = (eventId: string) => {
  const event = events.value.find((e) => e.id === eventId);
  if (event) {
    Swal.fire({
      title: "Are you sure?",
      text: `Do you want to delete the event "${event.title}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#4f46e5",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        deleteEvent(eventId);
        Swal.fire("Deleted!", "Your event has been deleted.", "success");
      }
    });
  }
};

const selectHolidayCountry = () => {
  emit("view-changed", {
    view: currentView.value,
    selected_date: selectedDate.value,
    selected_holiday_country_code: dashboardStore.selectedHolidayCountryCode,
  });
};

const setDefaultHolidays = () => {
  const year = selectedDate.value.getFullYear();

  if (dashboardStore.selectedHolidayCountryCode === "DK") {
    // Danish holidays
    holidays.value = [
      {
        id: "holiday1",
        name: "New Year's Day",
        date: `${year}-01-01`,
        type: HolidayType.Public,
      },
      {
        id: "holiday2",
        name: "Maundy Thursday",
        date: `${year}-04-06`, // Example date
        type: HolidayType.Public,
      },
      // ... other Danish holidays
    ];
  } else if (dashboardStore.selectedHolidayCountryCode === "PK") {
    // Pakistani holidays
    holidays.value = [
      {
        id: "holiday1",
        name: "Kashmir Day",
        date: `${year}-02-05`,
        type: HolidayType.Public,
      },
      {
        id: "holiday2",
        name: "Pakistan Day",
        date: `${year}-03-23`,
        type: HolidayType.Public,
      },
      // ... other Pakistani holidays
    ];
  }
};

const handleShowMore = (date: Date) => {
  const eventsForDay = getEventsForDay(date);

  if (eventsForDay.length > 15) {
    // If there are too many events, show the modal
    tooManyEventsDate.value = date;
    tooManyEvents.value = eventsForDay;
    showTooManyEventsModal.value = true;
  } else {
    // Otherwise switch to day view
    switchToDayView(date);
  }
};

const closeTooManyEventsModal = () => {
  showTooManyEventsModal.value = false;
  tooManyEventsDate.value = null;
  tooManyEvents.value = [];
};

const getEventsForDay = (date: Date) => {
  return events.value.filter((event) => {
    const eventDate = parseISO(event.start);
    return isSameDay(eventDate, date);
  });
};

const changeView = (view) => {
  // Preserve the current selected date when changing views
  const currentSelectedDate = selectedDate.value;

  currentView.value = view;

  // Ensure currentDate is also updated to match selectedDate
  currentDate.value = new Date(currentSelectedDate);

  emit("view-changed", {
    view: view,
    selected_date: currentSelectedDate,
    selected_holiday_country_code: dashboardStore.selectedHolidayCountryCode,
  });
};

// Autocomplete search for order ID
const searchOrdersWrapper = async (query: string, signal?: AbortSignal) => {
  await searchOrders(query, signal);
  return [];
};

const { search: immediateSearch, isLoading: isSearchLoading } =
  useDebouncedSearch(searchOrdersWrapper, { minLength: 0 });

const onOrderSearch = async (query: string, loading: (b: boolean) => void) => {
  loading(true);
  await immediateSearch(query);
  loading(false);
};

watch(
  () => selectedOrder.value, // explicitly watch the ref's value
  (order, prevOrder) => {
    if (order !== prevOrder && order && dashboardStore.setSelectedOrder) {
      dashboardStore.setSelectedOrder(order);
    }
  },
);

// Watch for orderInfo changes to update form fields for new events
watch(
  () => orderInfo.value,
  (newOrderInfo, oldOrderInfo) => {
    // Only update form fields if we're not editing an existing event and orderInfo actually changed
    if (
      !editingEvent.value &&
      newOrderInfo &&
      Object.keys(newOrderInfo).length > 0
    ) {
      // Only update if orderInfo actually has meaningful data and it's different from before
      if (newOrderInfo.status && newOrderInfo.status !== oldOrderInfo?.status) {
        eventForm.value.status = newOrderInfo.status as EventFormData["status"];
      }
      if (newOrderInfo.notes && newOrderInfo.notes !== oldOrderInfo?.notes) {
        eventForm.value.notes = newOrderInfo.notes;
      }
    }
  },
  { deep: true },
);

function formatShippingAddress(addr) {
  if (dashboardStore.formatShippingAddress) {
    return dashboardStore.formatShippingAddress(addr);
  }
  // Fallback implementation
  if (!addr) return "";
  return [
    addr.address1 || addr.address_1,
    addr.address2 || addr.address_2,
    addr.city,
    addr.zip_code || addr.postcode,
    addr.country,
    addr.company,
  ]
    .filter(Boolean)
    .join(", ");
}

function resetEventForm() {
  eventForm.value = {
    id: "",
    title: "",
    start: format(new Date(), "yyyy-MM-dd"),
    time: format(new Date(), "HH:mm"),
    type: CalendarEventType.Event,
    status: OrderStatus.Pending,
    notes: "",
    allDay: false,
  };
  if (dashboardStore.resetOrderInfo) {
    dashboardStore.resetOrderInfo();
  }
}

// Computed properties
const weekDays = computed(() => ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"]);

const miniCalendarDays = computed(() => {
  const days: CalendarDay[] = [];
  const monthStart = startOfMonth(currentDate.value);
  const monthEnd = endOfMonth(currentDate.value);
  const startDate = startOfWeek(monthStart);
  const endDate = endOfWeek(monthEnd);

  let day = startDate;
  while (day <= endDate) {
    days.push({
      date: new Date(day),
      dayNumber: day.getDate(),
      month: isSameMonth(day, monthStart)
        ? "current"
        : isBefore(day, monthStart)
          ? "prev"
          : "next",
    });
    day = addDays(day, 1);
  }

  return days;
});

const calendarTitle = computed(() => {
  if (currentView.value === "day") {
    return format(selectedDate.value, "MMMM d, yyyy");
  } else if (currentView.value === "week") {
    const weekStart = startOfWeek(selectedDate.value);
    const weekEnd = endOfWeek(selectedDate.value);
    return `Week ${getWeek(selectedDate.value)} (${format(weekStart, "MMM d")} - ${format(weekEnd, "MMM d, yyyy")})`;
  } else if (currentView.value === "month") {
    return format(selectedDate.value, "MMMM yyyy");
  } else {
    return format(selectedDate.value, "yyyy");
  }
});

const viewOptions = computed(() => [
  { label: "Week", value: "week" as const },
  { label: "Month", value: "month" as const },
  { label: "Year", value: "year" as const },
]);

const yearOptions = computed(() => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = currentYear - 10; i <= currentYear + 10; i++) {
    years.push(i);
  }
  return years;
});

// In filteredEvents, cast each event to LocalEvent
const filteredEvents = computed(() =>
  events.value
    .filter(
      (event) =>
        (!props.eventTypeFilter || event.type === props.eventTypeFilter) &&
        (!props.eventStatusFilter || event.status === props.eventStatusFilter),
    )
    .map(
      (event) =>
        ({
          ...(event as LocalEvent),
          status: (event.status as OrderStatus) || OrderStatus.Pending,
        }) as LocalEvent,
    ),
);

const upcomingEvents = computed(() => {
  const today = startOfDay(new Date());
  return events.value
    .filter((event) => {
      const eventDate = parseISO(event.start);
      return isAfter(eventDate, today) || isSameDay(eventDate, today);
    })
    .sort((a, b) => {
      return new Date(a.start).getTime() - new Date(b.start).getTime();
    })
    .slice(0, 5);
});

// Methods
const formatMonth = (date: Date) => {
  return format(date, "MMMM yyyy");
};

const formatDate = (dateStr: string) => {
  return format(parseISO(dateStr), "MMM d, yyyy");
};

const goToToday = () => {
  const today = new Date();
  selectedDate.value = today;
  currentDate.value = today;

  // Emit view-changed to trigger API call
  emit("view-changed", {
    view: currentView.value,
    selected_date: selectedDate.value,
    selected_holiday_country_code: dashboardStore.selectedHolidayCountryCode,
  });
};

const prevMonth = () => {
  currentDate.value = subMonths(currentDate.value, 1);
  selectedDate.value = currentDate.value;

  // Emit view-changed to trigger API call
  emit("view-changed", {
    view: currentView.value,
    selected_date: selectedDate.value,
    selected_holiday_country_code: dashboardStore.selectedHolidayCountryCode,
  });
};

const nextMonth = () => {
  currentDate.value = addMonths(currentDate.value, 1);
  selectedDate.value = currentDate.value;

  // Emit view-changed to trigger API call
  emit("view-changed", {
    view: currentView.value,
    selected_date: selectedDate.value,
    selected_holiday_country_code: dashboardStore.selectedHolidayCountryCode,
  });
};

const selectDate = (date: Date) => {
  const previousDate = selectedDate.value;
  selectedDate.value = date;
  currentDate.value = date;
  emit("date-selected", date);

  // If the selected date is in a different month, emit view-changed to trigger API call
  if (
    previousDate &&
    (previousDate.getFullYear() !== date.getFullYear() ||
      previousDate.getMonth() !== date.getMonth())
  ) {
    emit("view-changed", {
      view: currentView.value,
      selected_date: selectedDate.value,
      selected_holiday_country_code: dashboardStore.selectedHolidayCountryCode,
    });
  }
};

const selectMonth = (date: Date) => {
  selectedDate.value = date;
  currentDate.value = date;
  currentView.value = "month";
  emit("view-changed", {
    view: "month",
    selected_date: selectedDate.value,
    selected_holiday_country_code: dashboardStore.selectedHolidayCountryCode,
  });
};

const prevPeriod = () => {
  if (currentView.value === "day") {
    selectedDate.value = addDays(selectedDate.value, -1);
  } else if (currentView.value === "week") {
    selectedDate.value = addDays(selectedDate.value, -7);
  } else if (currentView.value === "month") {
    selectedDate.value = subMonths(selectedDate.value, 1);
  } else {
    selectedDate.value = subYears(selectedDate.value, 1);
  }
  currentDate.value = selectedDate.value;

  // Emit view-changed to trigger API call
  emit("view-changed", {
    view: currentView.value,
    selected_date: selectedDate.value,
    selected_holiday_country_code: dashboardStore.selectedHolidayCountryCode,
  });
};

const nextPeriod = () => {
  if (currentView.value === "day") {
    selectedDate.value = addDays(selectedDate.value, 1);
  } else if (currentView.value === "week") {
    selectedDate.value = addDays(selectedDate.value, 7);
  } else if (currentView.value === "month") {
    selectedDate.value = addMonths(selectedDate.value, 1);
  } else {
    selectedDate.value = addYears(selectedDate.value, 1);
  }
  currentDate.value = selectedDate.value;

  // Emit view-changed to trigger API call
  emit("view-changed", {
    view: currentView.value,
    selected_date: selectedDate.value,
    selected_holiday_country_code: dashboardStore.selectedHolidayCountryCode,
  });
};

// Define a local searchOrders function for the wrapper
const searchOrders = async (query: string, signal?: AbortSignal) => {
  if (dashboardStore.searchOrders) {
    await dashboardStore.searchOrders(query, signal);
  }
};

const eventDialogTitle = computed(() => {
  if (editingEvent.value?.type === CalendarEventType.Order) {
    return "View Order";
  }
  if (editingEvent.value) {
    return "Update Event";
  }
  return "Add Event";
});
</script>

<style lang="scss">
.calendar-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.mini-day {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 768px) {
  .calendar-container {
    height: auto;
    min-height: 100vh;
  }
}
</style>
